import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "adeal" }
const _hoisted_2 = {
  key: 0,
  class: "adeal__error"
}
const _hoisted_3 = { class: "adeal__grid" }
const _hoisted_4 = {
  class: "sui-card sui-card--flush",
  "data-tour": "admin-deals-list"
}
const _hoisted_5 = { class: "sui-toolbar" }
const _hoisted_6 = { class: "adeal__section-title" }
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "sui-toolbar adeal__period" }
const _hoisted_10 = { class: "adeal__period-field" }
const _hoisted_11 = { class: "adeal__period-field" }
const _hoisted_12 = {
  key: 0,
  class: "adeal__list"
}
const _hoisted_13 = ["onDragstart", "onDrop"]
const _hoisted_14 = { class: "sui-prod" }
const _hoisted_15 = ["src", "alt"]
const _hoisted_16 = {
  key: 1,
  class: "adeal__noimg"
}
const _hoisted_17 = { class: "sui-prod__name" }
const _hoisted_18 = { class: "sui-table__sub" }
const _hoisted_19 = { class: "adeal__item-actions" }
const _hoisted_20 = ["disabled", "onClick"]
const _hoisted_21 = ["disabled", "onClick"]
const _hoisted_22 = ["onClick"]
const _hoisted_23 = {
  key: 1,
  class: "sui-empty"
}
const _hoisted_24 = {
  class: "sui-card sui-card--flush",
  "data-tour": "admin-deals-picker"
}
const _hoisted_25 = { class: "sui-toolbar adeal__search-bar" }
const _hoisted_26 = { class: "sui-search" }
const _hoisted_27 = ["disabled"]
const _hoisted_28 = {
  key: 0,
  class: "adeal__candidates"
}
const _hoisted_29 = { class: "sui-prod" }
const _hoisted_30 = ["src", "alt"]
const _hoisted_31 = {
  key: 1,
  class: "adeal__noimg"
}
const _hoisted_32 = { class: "sui-prod__name" }
const _hoisted_33 = { class: "sui-table__sub" }
const _hoisted_34 = ["disabled", "onClick"]
const _hoisted_35 = {
  key: 1,
  class: "sui-empty"
}

import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  fetchAdminDeals,
  fetchAdminProducts,
  saveAdminDeals,
} from '@/api/admin'
import type { ServerMallProduct } from '@/api/mallProduct'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { formatWon } from '@/constants/sellerData'

const MAX_DEALS = 12


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminDealsPage',
  setup(__props) {

const { notify } = useAdminProducts()

const draft = ref<ServerMallProduct[]>([])
const savedCodes = ref<string[]>([])
const savedPeriod = ref({ startAt: '', endAt: '' })
const dealStartAt = ref('')
const dealEndAt = ref('')
const candidates = ref<ServerMallProduct[]>([])
const keyword = ref('')
const loading = ref(false)
const searching = ref(false)
const saving = ref(false)
const errorMessage = ref('')

function toLocalInput(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toServerDate(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
}

const dirty = computed(() => {
  const current = draft.value.map((item) => item.code).join('|')
  return (
    current !== savedCodes.value.join('|') ||
    dealStartAt.value !== savedPeriod.value.startAt ||
    dealEndAt.value !== savedPeriod.value.endAt
  )
})

function isSelected(code: string): boolean {
  return draft.value.some((item) => item.code === code)
}

const dragIndex = ref<number | null>(null)

function move(index: number, delta: number) {
  const next = index + delta
  if (next < 0 || next >= draft.value.length) return
  const list = [...draft.value]
  const [item] = list.splice(index, 1)
  list.splice(next, 0, item)
  draft.value = list
}

function onDragStart(index: number) {
  dragIndex.value = index
}

function onDrop(index: number) {
  if (dragIndex.value === null || dragIndex.value === index) return
  const list = [...draft.value]
  const [item] = list.splice(dragIndex.value, 1)
  list.splice(index, 0, item)
  draft.value = list
  dragIndex.value = null
}

function removeAt(index: number) {
  const item = draft.value[index]
  if (!item) return
  if (!window.confirm(`'${item.name}'을(를) 특가 목록에서 제외할까요?`)) return
  draft.value = draft.value.filter((_, i) => i !== index)
}

function addProduct(product: ServerMallProduct) {
  if (isSelected(product.code) || draft.value.length >= MAX_DEALS) return
  if (!window.confirm(`'${product.name}'을(를) 특가에 추가할까요?`)) return
  draft.value = [...draft.value, product]
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  try {
    const products = await fetchAdminDeals()
    draft.value = products
    savedCodes.value = products.map((item) => item.code)
    const first = products[0]
    dealStartAt.value = toLocalInput(first?.dealStartAt)
    dealEndAt.value = toLocalInput(first?.dealEndAt)
    savedPeriod.value = { startAt: dealStartAt.value, endAt: dealEndAt.value }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function searchCandidates() {
  searching.value = true
  errorMessage.value = ''
  try {
    const { products } = await fetchAdminProducts({
      status: 'SELLING',
      keyword: keyword.value,
    })
    candidates.value = products.slice(0, 30)
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '상품 검색에 실패했습니다.'
  } finally {
    searching.value = false
  }
}

async function onSave() {
  if (!window.confirm('이달의 특가 구성을 저장할까요?')) return
  saving.value = true
  errorMessage.value = ''
  try {
    const { products, message } = await saveAdminDeals(
      draft.value.map((item) => item.code),
      {
        startAt: toServerDate(dealStartAt.value),
        endAt: toServerDate(dealEndAt.value),
      },
    )
    draft.value = products
    savedCodes.value = products.map((item) => item.code)
    dealStartAt.value = toLocalInput(products[0]?.dealStartAt)
    dealEndAt.value = toLocalInput(products[0]?.dealEndAt)
    savedPeriod.value = { startAt: dealStartAt.value, endAt: dealEndAt.value }
    notify(message || '이달의 특가를 저장했습니다.')
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '저장에 실패했습니다.'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  await reload()
  await searchCandidates()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[9] || (_cache[9] = _createElementVNode("p", { class: "adeal__lead" }, " 첫 번째 상품이 몰 홈 「이달의 특가」 히어로로 크게 노출됩니다. 비워 두면 판매가 많은 상품이 자동으로 채워집니다. ", -1)),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
          _createVNode(AppIcon, {
            icon: "mdi-alert-outline",
            size: 16
          }),
          _createTextVNode(" " + _toDisplayString(errorMessage.value), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("section", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("strong", _hoisted_6, "노출 목록 " + _toDisplayString(draft.value.length) + "개", 1),
          _cache[4] || (_cache[4] = _createElementVNode("span", { class: "sui-toolbar__spacer" }, null, -1)),
          _createElementVNode("button", {
            type: "button",
            class: "sui-btn",
            disabled: loading.value || !dirty.value,
            onClick: reload
          }, " 변경 취소 ", 8, _hoisted_7),
          _createElementVNode("button", {
            type: "button",
            class: "sui-btn sui-btn--primary",
            disabled: saving.value || !dirty.value,
            onClick: onSave
          }, _toDisplayString(saving.value ? '저장 중…' : '특가 저장'), 9, _hoisted_8)
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("label", _hoisted_10, [
            _cache[5] || (_cache[5] = _createElementVNode("span", null, "특가 시작", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dealStartAt).value = $event)),
              type: "datetime-local"
            }, null, 512), [
              [_vModelText, dealStartAt.value]
            ])
          ]),
          _createElementVNode("label", _hoisted_11, [
            _cache[6] || (_cache[6] = _createElementVNode("span", null, "특가 종료", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dealEndAt).value = $event)),
              type: "datetime-local"
            }, null, 512), [
              [_vModelText, dealEndAt.value]
            ])
          ]),
          _cache[7] || (_cache[7] = _createElementVNode("p", { class: "adeal__period-hint" }, "비우면 상시 노출입니다. 저장 시 목록 전체에 적용됩니다.", -1))
        ]),
        (draft.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(draft.value, (product, index) => {
                return (_openBlock(), _createElementBlock("article", {
                  key: product.code,
                  class: "adeal__item",
                  draggable: "true",
                  onDragstart: ($event: any) => (onDragStart(index)),
                  onDragover: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"])),
                  onDrop: ($event: any) => (onDrop(index))
                }, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["adeal__rank", { 'is-hero': index === 0 }]),
                    title: "끌어다 순서를 바꿀 수 있습니다"
                  }, _toDisplayString(index === 0 ? '히어로' : index + 1), 3),
                  _createElementVNode("span", _hoisted_14, [
                    (product.thumbnail)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          class: "sui-prod__img",
                          src: product.thumbnail,
                          alt: product.name,
                          loading: "lazy"
                        }, null, 8, _hoisted_15))
                      : (_openBlock(), _createElementBlock("span", _hoisted_16, [
                          _createVNode(AppIcon, {
                            icon: "mdi-image-off-outline",
                            size: 16
                          })
                        ])),
                    _createElementVNode("span", null, [
                      _createElementVNode("span", _hoisted_17, _toDisplayString(product.name), 1),
                      _createElementVNode("span", _hoisted_18, _toDisplayString(product.code) + " · " + _toDisplayString(product.storeName) + " · " + _toDisplayString(_unref(formatWon)(product.price)) + "원 ", 1)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "sui-btn sui-btn--sm",
                      disabled: index === 0,
                      "aria-label": "위로",
                      onClick: ($event: any) => (move(index, -1))
                    }, [
                      _createVNode(AppIcon, {
                        icon: "mdi-chevron-up",
                        size: 16
                      })
                    ], 8, _hoisted_20),
                    _createElementVNode("button", {
                      type: "button",
                      class: "sui-btn sui-btn--sm",
                      disabled: index === draft.value.length - 1,
                      "aria-label": "아래로",
                      onClick: ($event: any) => (move(index, 1))
                    }, [
                      _createVNode(AppIcon, {
                        icon: "mdi-chevron-down",
                        size: 16
                      })
                    ], 8, _hoisted_21),
                    _createElementVNode("button", {
                      type: "button",
                      class: "sui-btn sui-btn--sm",
                      onClick: ($event: any) => (removeAt(index))
                    }, "제외", 8, _hoisted_22)
                  ])
                ], 40, _hoisted_13))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createVNode(AppIcon, {
                icon: "mdi-sale-outline",
                size: 32
              }),
              _createElementVNode("p", null, _toDisplayString(loading.value ? '불러오는 중입니다…' : '지정된 특가 상품이 없습니다.'), 1)
            ]))
      ]),
      _createElementVNode("section", _hoisted_24, [
        _cache[8] || (_cache[8] = _createElementVNode("div", { class: "sui-toolbar" }, [
          _createElementVNode("strong", { class: "adeal__section-title" }, "상품 추가")
        ], -1)),
        _createElementVNode("div", _hoisted_25, [
          _createElementVNode("label", _hoisted_26, [
            _createVNode(AppIcon, {
              icon: "mdi-magnify",
              size: 17
            }),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((keyword).value = $event)),
              type: "search",
              placeholder: "판매중 상품명 · 코드 · 업체 검색",
              onKeydown: _withKeys(searchCandidates, ["enter"])
            }, null, 544), [
              [
                _vModelText,
                keyword.value,
                void 0,
                { trim: true }
              ]
            ])
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "sui-btn",
            disabled: searching.value,
            onClick: searchCandidates
          }, " 검색 ", 8, _hoisted_27)
        ]),
        (candidates.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(candidates.value, (product) => {
                return (_openBlock(), _createElementBlock("article", {
                  key: product.code,
                  class: "adeal__candidate"
                }, [
                  _createElementVNode("span", _hoisted_29, [
                    (product.thumbnail)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          class: "sui-prod__img",
                          src: product.thumbnail,
                          alt: product.name,
                          loading: "lazy"
                        }, null, 8, _hoisted_30))
                      : (_openBlock(), _createElementBlock("span", _hoisted_31, [
                          _createVNode(AppIcon, {
                            icon: "mdi-image-off-outline",
                            size: 16
                          })
                        ])),
                    _createElementVNode("span", null, [
                      _createElementVNode("span", _hoisted_32, _toDisplayString(product.name), 1),
                      _createElementVNode("span", _hoisted_33, _toDisplayString(product.code) + " · " + _toDisplayString(_unref(formatWon)(product.price)) + "원 ", 1)
                    ])
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "sui-btn sui-btn--sm sui-btn--primary",
                    disabled: isSelected(product.code) || draft.value.length >= MAX_DEALS,
                    onClick: ($event: any) => (addProduct(product))
                  }, _toDisplayString(isSelected(product.code) ? '추가됨' : '추가'), 9, _hoisted_34)
                ]))
              }), 128))
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_35, [
              _createVNode(AppIcon, {
                icon: "mdi-magnify",
                size: 28
              }),
              _createElementVNode("p", null, _toDisplayString(searching.value ? '검색 중…' : '판매중 상품을 검색해 특가에 추가하세요.'), 1)
            ]))
      ])
    ])
  ]))
}
}

})